import type {
    ComposableOptionsBase,
    Registration,
    SiteMembershipContext
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination,
    PaginatedRequest
} from 'x1-base/types';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import { omit as lo_omit } from 'es-toolkit';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import type { NitroFetchOptions } from 'nitropack';

export interface UseRegistrationsOptions extends ComposableOptionsBase {
    siteMembershipContext?: SiteMembershipContext;
}
export function useRegistrations(options: Partial<UseRegistrationsOptions> = {}) {
    const apiBasePath = `/v1/registrations`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch();

    async function getRegistrations(args: PaginatedRequest): Promise<PaginatedPayload<Partial<Registration>> | Error> {
        const url = args?.url ?? `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, {
            expand: ['sku']
        });

        let paginatedPayload: PaginatedPayload<Partial<Registration>>;

        try {
            const response = await $_fetch<CustomPagination<Partial<Registration>>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getRegistration(uid: string): Promise<Partial<Registration> | Error> {
        const url = `${apiBasePath}/${uid}/`;

        try {
            const response = await $_fetch<Partial<Registration>>(url);
            return response;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function createRegistrations(regObj: Partial<Registration>): Promise<Partial<Registration> | Error> {
        const url = `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: regObj
        };

        try {
            const response = await $_fetch<Partial<Registration>>(url, config);
            return response;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function updateRegistrations(
        uid: string,
        regObj: Partial<Registration>
    ): Promise<Partial<Registration> | Error> {
        const url = `${apiBasePath}/${uid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'PATCH',
            body: regObj
        };

        try {
            const response = await $_fetch<Partial<Registration>>(url, config);
            return response;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    return {
        getRegistrations,
        getRegistration,
        createRegistrations,
        updateRegistrations
    };
}
